import { notification } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLazyGetClientServiceByIdQuery } from '@app/api/ClientServices';
import {
  ADD_SERVICE_PATH,
  DESKTOP,
  EDIT_SERVICE_PATH,
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EDIT,
} from '@app/constants';
import {
  useAccessRights,
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { LeavePageContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import {
  ClientServiceParams,
  ClientServiceProductUsages,
  ClientsServiceEmployees,
} from '@pages/Clients/EditClientService/components';

import type { TabsProps } from 'antd/lib';

export const useEditClientService = () => {
  const { innerWidth } = useWindowSize();
  const {
    userAccessRights: {
      Warehouse: { IsOn },
    },
    organizationAccessRights,
  } = useNewUserAccessRights();

  const warehouseModuleAllowed =
    organizationAccessRights.find(({ RightType }) => RightType === 9)
      ?.Allowed || false;

  const isDesktop = innerWidth > DESKTOP;
  const { hasUserRight: isCreationRight } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: isEditionRight } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_EDIT
  );
  const [searchParams] = useSearchParams();
  const editableServiceId = Number(searchParams.get('id'));
  const initialTab = Number(searchParams.get('key'));
  const { showNotification } = useNotification();
  const { activeBranchId } = useUserPrint();

  const navigate = useNavigate();

  const [getClientServiceById] = useLazyGetClientServiceByIdQuery();

  const [activeKey, setActiveKey] = useState('1');
  const [serviceName, setServiceName] = useState('');

  const [, contextHolder] = notification.useNotification();

  const { checkIsDataUpdated } = useContext(LeavePageContext);

  const isUnauthorized = useMemo(
    () =>
      (!editableServiceId && !isCreationRight) ||
      (editableServiceId && !isEditionRight),
    [editableServiceId, isCreationRight, isEditionRight]
  );

  const initTabHandler = useCallback(() => {
    setActiveKey(String(initialTab));
  }, [initialTab]);

  const handleTabClick = useCallback(
    (key: string) => {
      const path = editableServiceId
        ? `${EDIT_SERVICE_PATH}?id=${editableServiceId}&key=${key}`
        : `${ADD_SERVICE_PATH}?key=${key}`;

      checkIsDataUpdated({ path, navigate });
    },
    [checkIsDataUpdated, editableServiceId, navigate]
  );

  const fetchClientService = useCallback(async () => {
    if (!activeBranchId || !editableServiceId) return;
    try {
      const { Name } = await getClientServiceById({
        ActiveBranchId: activeBranchId,
        serviceId: editableServiceId,
      }).unwrap();

      setServiceName(Name || 'Без имени');
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    getClientServiceById,
    activeBranchId,
    editableServiceId,
    showNotification,
  ]);

  const techMapTabKey: TabsProps['items'] = useMemo(() => {
    return IsOn && warehouseModuleAllowed
      ? [
          {
            label: generateSpanWithDataTestId(
              'Технологическая карта',
              editableServiceId
                ? 'client-service-product-usages-tab'
                : 'not-active-tab'
            ),
            key: '3',
            children: <ClientServiceProductUsages />,
            disabled: !editableServiceId,
          },
        ]
      : [];
  }, [IsOn, editableServiceId, warehouseModuleAllowed]);

  const tabs: TabsProps['items'] = useMemo(
    () => [
      {
        label: generateSpanWithDataTestId(
          'Основные параметры',
          'main-parameters-tab'
        ),
        key: '1',
        children: <ClientServiceParams />,
      },
      {
        label: generateSpanWithDataTestId(
          'Исполнители',
          editableServiceId ? 'tab-executors' : 'not-active-tab'
        ),
        key: '2',
        children: <ClientsServiceEmployees />,
        disabled: !editableServiceId,
      },
      ...techMapTabKey,
    ],
    [editableServiceId, techMapTabKey]
  );

  return {
    contextHolder,
    handleTabClick,
    activeKey,
    isDesktop,
    isUnauthorized,
    initTabHandler,
    tabs,
    fetchClientService,
    serviceName,
  };
};
